import React from 'react';
import { useTranslation } from 'react-i18next';

import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { list } from 'aws-amplify/storage';
import { fetchAuthSession } from 'aws-amplify/auth';
import S3Image from './S3Image';
import { handleImageSelect } from '../utils/clientUtils';
import { useNavigate } from 'react-router-dom';

async function listDir(company, deviceTypeID, boardID) {
    const result = await list({
        path: `gtms4/company=${company}/device=${deviceTypeID}/board=${boardID}/`,
        options: {
            listAll: true,
            bucket: 'upload',
        }
    });
    if (result && result.items) {
        console.log("List of files:", result);
        return result.items.map(item => item.path).sort((a, b) => a.split('/').pop().localeCompare(b.split('/').pop()));
    }
    else {
        console.log("No items found for device:", deviceTypeID, "board:", boardID);
        console.log(result);
    }
}

function ImageSelection({ deviceID, deviceTypeID, boardID, boardHeight, handleClose }) {
    const { t } = useTranslation();

    const [imageList, setImageList] = React.useState([]);
    const [selectError, setSelectError] = React.useState(null);
    const [canUpload, setCanUpload] = React.useState(false);
    const navigate = useNavigate();

    React.useEffect(() => {
        async function fetchImages() {
            const session = await fetchAuthSession();
            const company = session.tokens?.idToken?.payload?.['custom:company'];
            const images = await listDir(company, deviceTypeID, boardID);
            setImageList(images);
        }
        fetchImages();
    }, [deviceTypeID, boardID]);

    React.useEffect(() => {
        async function checkGroup() {
            const session = await fetchAuthSession();
            const groupsRaw = session.tokens?.idToken?.payload?.['cognito:groups'] ?? '';
            const groups = Array.isArray(groupsRaw)
                ? groupsRaw
                : String(groupsRaw).replace(/^\[|\]$/g, '').split(/[\s,]+/).filter(Boolean);
            setCanUpload(groups.includes('image_management'));
        }
        checkGroup();
    }, []);

    return (
        <Box>
            <DialogTitle id="alert-dialog-title" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {t('imageSelection.selectImage')}
                {canUpload && (
                    <Tooltip title={t('imageSelection.uploadNewImage')}>
                        <IconButton
                            size="small"
                            onClick={() => {
                                handleClose();
                                navigate(`/images/upload?device=${deviceTypeID}&board=${boardID}`);
                            }}
                        >
                            <AddCircleOutlineIcon />
                        </IconButton>
                    </Tooltip>
                )}
            </DialogTitle>
            <DialogContent>
                {selectError && (
                    <Typography color="error" variant="body2" sx={{ mb: 1 }}>{selectError}</Typography>
                )}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(80px, 1fr))',
                        gap: 2,
                    }}
                >
                    {imageList && imageList.length > 0 ? (
                        imageList.map((img, idx) => (
                            <S3Image
                                key={img}
                                src={img}
                                alt={`Image ${idx}`}
                                boardHeight={boardHeight}
                                sx={{ height: boardHeight, width: '100%' }}
                                onClick={async () => {
                                    setSelectError(null);
                                    try {
                                        await handleImageSelect(img, deviceID, boardID);
                                        handleClose(img);
                                    } catch (e) {
                                        setSelectError(e.message);
                                    }
                                }}
                            />
                        ))
                    ) : (
                        <Box sx={{ gridColumn: '1 / -1' }}>{t('imageSelection.noImages')}</Box>
                    )}
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} autoFocus>
                    {t('imageSelection.cancel')}
                </Button>
            </DialogActions>
        </Box>
    );
}

export default ImageSelection;